import { useState } from 'react';
import type { ProposalHeading } from '../../types/proposal';
import type { Tender } from '../../types/tender';
import { logTenderActivity } from '../../utils/activityLogger';

const EMPTY_ARRAY: never[] = [];

export const useStructureCsv = (
  proposalStructure: ProposalHeading[],
  setProposalStructure: React.Dispatch<React.SetStateAction<ProposalHeading[]>>,
  tender: Tender | null,
  showSnackbar: (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration?: number
  ) => void
) => {
  const [isUploadingStructure, setIsUploadingStructure] = useState<boolean>(false);

  const handleStructureFileUpload = async (file: File): Promise<void> => {
    setIsUploadingStructure(true);
    try {
      const text = await file.text();
      let parsedStructure: ProposalHeading[] = [];

      if (file.name.endsWith('.csv')) {
        const lines = text.split('\n').filter((line) => line.trim());

        if (lines.length > 51) {
          throw new Error('CSV file too large. Maximum 50 rows allowed (plus header).');
        }

        const headers = lines[0]?.split(',').map((h) => h.trim().toLowerCase()) || EMPTY_ARRAY;

        if (headers.length > 4) {
          throw new Error(
            'CSV file has too many columns. Maximum 4 columns allowed (title, level, enabled, focuser).'
          );
        }

        const titleIndex = headers.findIndex((h) => h.includes('title') || h.includes('name'));
        const levelIndex = headers.findIndex((h) => h.includes('level'));
        const enabledIndex = headers.findIndex((h) => h.includes('enabled'));
        const focuserIndex = headers.findIndex((h) => h.includes('focuser') || h.includes('focus'));

        if (titleIndex === -1) {
          throw new Error('CSV must contain a "title" or "name" column');
        }

        parsedStructure = lines.slice(1).map((line, index) => {
          const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => {
            let val = v.trim();
            if (val.startsWith('"') && val.endsWith('"')) {
              val = val.slice(1, -1).replace(/""/g, '"');
            }
            return val;
          });

          values.forEach((value, cellIndex) => {
            if (value.length > 200) {
              throw new Error(
                `Cell content too long at row ${index + 2}, column ${cellIndex + 1}. Maximum 200 characters allowed.`
              );
            }
          });

          const levelValue = levelIndex === -1 ? 1 : parseInt(values[levelIndex] || '1', 10) || 1;
          const newItem: ProposalHeading = {
            id: `imported-${Date.now()}-${index}`,
            title: values[titleIndex] || `Item ${index + 1}`,
            type: 'custom',
            enabled: enabledIndex === -1 ? true : values[enabledIndex]?.toLowerCase() !== 'false',
            level: Math.max(1, Math.min(4, levelValue)) as 1 | 2 | 3 | 4,
          };

          if (focuserIndex !== -1 && values[focuserIndex]) {
            newItem.focuser = values[focuserIndex].substring(0, 1000);
          }

          return newItem;
        });
      } else {
        throw new Error('Unsupported file format. Please use .csv files.');
      }

      if (parsedStructure.length === 0) {
        throw new Error('No valid structure items found in the file.');
      }

      setProposalStructure(parsedStructure);
      showSnackbar(`Successfully imported ${parsedStructure.length} structure items!`, 'success');
      if (tender?.id) {
        logTenderActivity.updated({
          id: tender.id,
          title: tender.title || '',
          updateType: 'structure_import',
          reason: `Imported ${parsedStructure.length} items from CSV`,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to parse structure file';
      showSnackbar(`Error importing structure: ${errorMessage}`, 'error');
    } finally {
      setIsUploadingStructure(false);
    }
  };

  const downloadStructureTemplate = (): void => {
    const template = proposalStructure.map((item) => ({
      title: item.title,
      level: item.level,
      enabled: item.enabled,
      focuser: item.focuser || '',
    }));

    const escapeCsvCell = (cell: string): string => {
      if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
        const escapedStr = cell.replace(/"/g, '""');
        return `"${escapedStr}"`;
      }
      return cell;
    };

    const csvContent =
      'title,level,enabled,focuser\r\n' +
      template
        .map(
          (item) =>
            `${escapeCsvCell(item.title)},${item.level},${item.enabled},${escapeCsvCell(item.focuser)}`
        )
        .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'proposal-structure-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    isUploadingStructure,
    handleStructureFileUpload,
    downloadStructureTemplate,
  };
};
